import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { postMessage } from "core/utils/tracker";
import {
  trackAdEvent,
  AD_REQUESTED,
  AD_FILLED,
  AD_NO_FILL,
} from "core/utils/adAnalytics";
import { useQuery } from "@apollo/client";
import { useContentEmbedId } from "core/components/ContentEmbedProvider";
import { SponsorVideoQuery } from "core/queries";
import Video from "./Video";
import Vast from "./Vast";
import Yandex from "./Yandex";
import AdfoxBanner from "./AdfoxBanner";

const NOT_FOUND = "NOT_FOUND";

export default function SponsorVideo({
  contentId,
  sportId,
  rightholderId,
  videoPlace,
  chatMessagePlace,
  fullscreenTarget,
  onFinish,
  onStart,
  onChatAdv,
  onNotFound,
  muted,
  shareUrl,
}) {
  const embedId = useContentEmbedId();
  const [excludeCreativeIds, setExcludeCreativeIds] = useState([]);

  /**
   * Начало воронки: запрос кампании ушёл. Повторный запрос после ошибки
   * креатива (excludeCreativeIds) тоже считается запросом, иначе показы
   * со второй попытки выглядят как взявшиеся из ниоткуда.
   */
  useEffect(() => {
    if (!process.browser) return;

    trackAdEvent(AD_REQUESTED, {
      silent: true,
      place: videoPlace,
      contentId,
      attempt: excludeCreativeIds.length + 1,
    });
  }, [videoPlace, contentId, excludeCreativeIds.length]);

  const { data, loading } = useQuery(SponsorVideoQuery, {
    skip: !process.browser,
    fetchPolicy: "network-only",
    variables: {
      contentId,
      rightholderId,
      embedId,
      adfoxBannerPlace: videoPlace,
      vastPlace: videoPlace,
      videoPlace,
      chatMessagePlace,
      excludeCreativeIds,
    },
    onCompleted(data) {
      // console.log("Sponsor Campaign", data?.sponsorCampaign);

      const notFound =
        !data?.sponsorCampaign?.video &&
        !data?.sponsorCampaign?.vast &&
        !data?.sponsorCampaign?.adfoxBanner &&
        !data?.sponsorCampaign?.chatMessage;

      if (notFound) {
        onNotFound();
        postMessage({
          eventName: NOT_FOUND,
          creative: "SponsorVideo",
          place: videoPlace,
        });
        trackAdEvent(AD_NO_FILL, {
          silent: true,
          place: videoPlace,
          contentId,
        });
      } else {
        const campaign = data.sponsorCampaign;
        const creative =
          (campaign.adfoxBanner && "adfox") ||
          (campaign.video && "video") ||
          (campaign.vast?.yandex && "yandex") ||
          (campaign.vast && "vast") ||
          "chat";

        trackAdEvent(AD_FILLED, {
          silent: true,
          place: videoPlace,
          contentId,
          creative,
        });
      }

      if (data?.sponsorCampaign?.chatMessage) {
        onChatAdv(data.sponsorCampaign.chatMessage);
      }
    },
  });

  if (loading) return null;
  if (!data?.sponsorCampaign) return null;
  if (!process.browser) return null;

  const { video, vast, adfoxBanner } = data.sponsorCampaign;

  if (adfoxBanner) {
    const onError = () => {
      setExcludeCreativeIds((ids) => [...ids, adfoxBanner.id]);
    };

    return (
      <AdfoxBanner
        adfoxBanner={adfoxBanner}
        place={videoPlace}
        onStart={onStart}
        onFinish={onFinish}
        onError={onError}
      />
    );
  }

  if (video) {
    const onError = () => {
      setExcludeCreativeIds((ids) => [...ids, video.id]);
    };

    // hardcode for test period with wrestlingtv
    const isDisableable =
      videoPlace.endsWith("ON_SITE") &&
      rightholderId === "5V2a_7I3_bzIVThUb5_R5g";

    return (
      <Video
        key={video.id}
        video={video}
        videoPlace={videoPlace}
        fullscreenTarget={fullscreenTarget}
        onFinish={onFinish}
        onStart={onStart}
        onError={onError}
        muted={muted}
        isDisableable={isDisableable}
      />
    );
  }

  if (vast) {
    const onError = () => {
      // console.log("onError", error);
      // console.log("exclude creative", vast.id);
      setExcludeCreativeIds((ids) => [...ids, vast.id]);
    };

    if (vast.yandex) {
      return (
        <Yandex
          key={vast.id}
          vast={vast}
          vastPlace={videoPlace}
          fullscreenTarget={fullscreenTarget}
          onFinish={onFinish}
          onStart={onStart}
          muted={muted}
          onError={onError}
          sportId={sportId}
          shareUrl={shareUrl}
        />
      );
    }

    const antiFrod = false;
    // data.sponsorCampaign.id === "09ed09zRvljGkGXz2Zz7fLJCh_2VdjlmvWyJLlSiT-g";

    return (
      <Vast
        key={vast.id}
        vast={vast}
        vastPlace={videoPlace}
        fullscreenTarget={fullscreenTarget}
        onFinish={onFinish}
        onStart={onStart}
        onError={onError}
        muted={muted}
        sportId={sportId}
        shareUrl={shareUrl}
        antiFrod={antiFrod}
      />
    );
  }

  return null;
}

SponsorVideo.defaultProps = {
  onFinish() {},
  onStart() {},
  onChatAdv() {},
  onNotFound() {},
};

SponsorVideo.propTypes = {
  contentId: PropTypes.string,
  sportId: PropTypes.string,
  rightholderId: PropTypes.string.isRequired,
  videoPlace: PropTypes.string.isRequired,
  chatMessagePlace: PropTypes.string,
  fullscreenTarget: PropTypes.string.isRequired,
  onFinish: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  onChatAdv: PropTypes.func.isRequired,
  onNotFound: PropTypes.func.isRequired,
  muted: PropTypes.bool,
  shareUrl: PropTypes.string,
};
